import React, { createContext, useContext, useMemo } from 'react';
import useStudents from '../hooks/useStudents';
import type { StudentType } from '../helpers/definitions';
import type { DBStudent } from '../../server/definitions';

type StudentsActions = {
  getAllStudents: () => Promise<StudentType[]>;
  addEmptyStudent: () => void;
  removeNthStudent: (i: number) => void;
  saveStudent: (student: StudentType) => Promise<boolean>;
  createStudent: (student: StudentType) => Promise<boolean>;
  createStudents: (students: StudentType[]) => Promise<boolean>;
  deleteStudent: (barcode: string) => Promise<boolean>;
  checkInStudent: (barcode: string) => Promise<boolean>;
  checkOutStudent: (barcode: string) => Promise<boolean>;
  scanStudent: (barcode: string) => Promise<DBStudent | null>;
};

type StudentsState = {
  students: StudentType[];
};

const StudentsStateContext = createContext<StudentsState | null>(null);
const StudentsActionsContext = createContext<StudentsActions | null>(null);

export function StudentsProvider({ children }: { children: React.ReactNode }) {
  const {
    students,
    getAllStudents,
    addEmptyStudent,
    removeNthStudent,
    saveStudent,
    createStudent,
    createStudents,
    deleteStudent,
    checkInStudent,
    checkOutStudent,
    scanStudent,
  } = useStudents();

  const stateValue = useMemo<StudentsState>(() => ({ students }), [students]);

  const actionsValue = useMemo<StudentsActions>(
    () => ({
      getAllStudents,
      addEmptyStudent,
      removeNthStudent,
      saveStudent,
      createStudent,
      createStudents,
      deleteStudent,
      checkInStudent,
      checkOutStudent,
      scanStudent,
    }),
    [
      getAllStudents,
      addEmptyStudent,
      removeNthStudent,
      saveStudent,
      createStudent,
      createStudents,
      deleteStudent,
      checkInStudent,
      checkOutStudent,
      scanStudent,
    ],
  );

  return (
    <StudentsStateContext.Provider value={stateValue}>
      <StudentsActionsContext.Provider value={actionsValue}>
        {children}
      </StudentsActionsContext.Provider>
    </StudentsStateContext.Provider>
  );
}

export function useStudentsState(): StudentsState {
  const ctx = useContext(StudentsStateContext);
  if (!ctx) throw new Error('useStudentsState must be used within StudentsProvider');
  return ctx;
}

export function useStudentsActions(): StudentsActions {
  const ctx = useContext(StudentsActionsContext);
  if (!ctx) throw new Error('useStudentsActions must be used within StudentsProvider');
  return ctx;
}
